/**
 * ops2026 — Team (staff access, 2026-09-25).
 *
 * Admins request new staff (they start "Pending approval" and cannot sign
 * in). Only the owner approves, changes roles, removes or reactivates,
 * each confirmed with a 6-digit code emailed to the owner. Nobody can act
 * on their own account, and the owner's account cannot be changed here.
 * Every step is in the Staff log and emailed to the owner.
 */
import { RefreshCw, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type { AdminStaffMember, StaffRole } from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { useAuth } from '../../app/auth-context.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import type { PillTone } from '../../components/atoms/Pill.js';
import { TableScroll } from '../../components/atoms/TableScroll.js';
import { formatIsoCompact } from '../../lib/format.js';
import { ROLE_LABEL, hasRole } from '../../lib/roles.js';
import { useC } from '../../lib/theme.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

type Assignable = 'admin' | 'support' | 'viewer';
const ASSIGNABLE: readonly Assignable[] = ['admin', 'support', 'viewer'];
const ROLE_HINT: Readonly<Record<Assignable, string>> = {
  admin: 'All operations: support logins with changes, approvals, billing, deletions, leads, settings.',
  support: 'Accounts, read-only support logins, smoke tests, health and jobs. No deletion or billing.',
  viewer: 'Metrics, health, jobs and CI only. No customer data.',
};

type CodeAction = 'approve' | 'change_role' | 'suspend' | 'reactivate';
const ACTION_ROUTE: Readonly<Record<CodeAction, 'approve' | 'role' | 'suspend' | 'reactivate'>> = {
  approve: 'approve',
  change_role: 'role',
  suspend: 'suspend',
  reactivate: 'reactivate',
};

interface Pending {
  readonly member: AdminStaffMember;
  readonly action: CodeAction;
  readonly role?: Assignable;
  readonly sent_to: string;
}

function statusTone(s: AdminStaffMember['status']): PillTone {
  switch (s) {
    case 'active': return 'lime';
    case 'pending_approval': return 'amber';
    case 'suspended': return 'coral';
    default: return 'neutral';
  }
}

const STATUS_LABEL: Readonly<Record<AdminStaffMember['status'], string>> = {
  active: 'active',
  pending_approval: 'pending approval',
  suspended: 'removed',
  denied: 'denied',
  expired: 'request expired',
};

function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    const r = err.server_reason ?? '';
    if (r === 'invalid' || r === 'expired' || r === 'too_many_attempts') {
      return r === 'too_many_attempts'
        ? 'Too many wrong codes. Send a new code.'
        : r === 'expired' ? 'That code expired. Send a new one.' : 'That code is not right.';
    }
    if (r.startsWith('already_')) return `That person is already ${r.replace('already_', '').replace('_', ' ')}.`;
    if (r === 'owner_email') return "That is the owner's email.";
    if (err.kind === 'forbidden') return 'Your role cannot do that.';
  }
  return 'Something went wrong. Try again.';
}

export function TeamView(): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const auth = useAuth();
  const me = auth.state.kind === 'authenticated' ? auth.state.staff : null;
  const isOwner = me?.role === 'owner';

  const [staff, setStaff] = useState<readonly AdminStaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Assignable>('support');
  const [requesting, setRequesting] = useState(false);

  const [pending, setPending] = useState<Pending | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [roleChoice, setRoleChoice] = useState<Record<string, Assignable>>({});

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setStaff((await api.listStaff()).staff);
    } catch (err) {
      setError(err instanceof ApiError && err.kind === 'forbidden' ? 'Admins and the owner can see the team.' : 'Failed to load the team.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (flash === null) return;
    const t = setTimeout(() => setFlash(null), 6000);
    return () => clearTimeout(t);
  }, [flash]);

  const request = async (): Promise<void> => {
    setRequesting(true);
    setError(null);
    try {
      await api.requestStaff({ email: email.trim(), ...(name.trim() ? { name: name.trim() } : {}), role });
      setFlash(`Request sent. ${email.trim()} stays disabled until the owner approves.`);
      setEmail('');
      setName('');
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setRequesting(false);
    }
  };

  const startAction = async (member: AdminStaffMember, action: CodeAction, newRole?: Assignable): Promise<void> => {
    setBusy(true);
    setDialogError(null);
    setCode('');
    try {
      const res = await api.staffStepUp({ action, staff_id: member.staff_id, ...(newRole ? { role: newRole } : {}) });
      setPending({ member, action, ...(newRole ? { role: newRole } : {}), sent_to: res.sent_to });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (): Promise<void> => {
    if (pending === null) return;
    setBusy(true);
    setDialogError(null);
    try {
      await api.staffAction(pending.member.staff_id, ACTION_ROUTE[pending.action], {
        code: code.trim(),
        ...(pending.role ? { role: pending.role } : {}),
      });
      const verb = { approve: 'approved', change_role: `changed to ${pending.role ?? ''}`, suspend: 'removed', reactivate: 'reactivated' }[pending.action];
      setFlash(`${pending.member.email} ${verb}.`);
      setPending(null);
      await load();
    } catch (err) {
      setDialogError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const deny = async (member: AdminStaffMember): Promise<void> => {
    setBusy(true);
    try {
      await api.staffAction(member.staff_id, 'deny', {});
      setFlash(`${member.email} denied.`);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const control = {
    fontFamily: sansFont, fontSize: 13, padding: '8px 10px', minHeight: 40,
    background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 3,
  };
  const btn = (tone: 'lime' | 'plain' | 'coral', enabled = true) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: sansFont, fontSize: 12,
    fontWeight: tone === 'lime' ? 600 : 500, padding: '6px 12px', minHeight: 36, borderRadius: 3,
    cursor: enabled ? 'pointer' : 'not-allowed', opacity: enabled ? 1 : 0.5,
    background: tone === 'lime' ? C.lime : 'transparent',
    color: tone === 'lime' ? C.ink : tone === 'coral' ? C.coralFg : C.text,
    border: `1px solid ${tone === 'lime' ? C.lime : tone === 'coral' ? C.coralFg : C.border}`,
  });
  const th = {
    textAlign: 'left' as const, fontFamily: monoFont, fontSize: 10, letterSpacing: 1,
    textTransform: 'uppercase' as const, color: C.muted, padding: '6px 8px',
    borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' as const,
  };
  const td = { padding: '10px 8px', fontSize: 12.5, color: C.body, borderBottom: `1px solid ${C.borderSoft ?? C.border}`, verticalAlign: 'top' as const };

  const canRequest = hasRole(me?.role as StaffRole | undefined, 'admin');

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase', marginBottom: 4 }}>
            Operations · Staff access
          </div>
          <h1 style={{ fontFamily: serifFont, fontSize: 28, color: C.text, margin: 0 }}>Team</h1>
        </div>
        <button type="button" onClick={() => void load()} style={btn('plain')}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {canRequest ? (
        <Card style={{ padding: 16, marginBottom: 12 }}>
          <div style={{ fontFamily: sansFont, fontSize: 13.5, fontWeight: 600, color: C.text, marginBottom: 4 }}>
            Add a staff member
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
            They stay disabled until the owner approves in ops with an emailed code. Requests expire after 7 days.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input aria-label="Email" placeholder="email@company.com" value={email}
              onChange={(e) => setEmail(e.target.value)} style={{ ...control, flex: '2 1 220px', minWidth: 0 }} />
            <input aria-label="Name" placeholder="Name (optional)" value={name}
              onChange={(e) => setName(e.target.value)} style={{ ...control, flex: '1 1 160px', minWidth: 0 }} />
            <select aria-label="Role" value={role} onChange={(e) => setRole(e.target.value as Assignable)}
              style={{ ...control, flex: '0 1 140px', minWidth: 0 }}>
              {ASSIGNABLE.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select>
            <button type="button" disabled={requesting || email.trim().length === 0}
              onClick={() => void request()} style={btn('lime', !requesting && email.trim().length > 0)}>
              <UserPlus size={13} /> {requesting ? 'Sending…' : 'Request access'}
            </button>
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 8 }}>{ROLE_HINT[role]}</div>
        </Card>
      ) : null}

      {flash !== null ? <div style={{ fontFamily: sansFont, fontSize: 13, color: C.limeFg, marginBottom: 10 }}>{flash}</div> : null}
      {error !== null ? (
        <Card style={{ padding: 14, marginBottom: 12 }}>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>{error}</div>
        </Card>
      ) : null}
      {loading && staff.length === 0 ? <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>Loading…</div> : null}

      {staff.length > 0 ? (
        <Card style={{ padding: 0 }}>
          <TableScroll minWidth={880}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sansFont }}>
              <thead>
                <tr>{['Staff member', 'Role', 'Status', 'Requested by', 'Last active', ''].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {staff.map((m) => {
                  const self = m.staff_id === me?.staff_id;
                  const locked = self || m.is_owner;
                  const disabled = m.status !== 'active';
                  return (
                    <tr key={m.staff_id} style={{ opacity: disabled && m.status !== 'pending_approval' ? 0.55 : 1 }}>
                      <td style={{ ...td, minWidth: 220 }}>
                        <div style={{ color: C.text, overflowWrap: 'anywhere' }}>{m.email}</div>
                        {m.name ? <div style={{ fontSize: 11.5, color: C.muted }}>{m.name}</div> : null}
                        {self ? <div style={{ fontSize: 11, color: C.muted }}>you</div> : null}
                      </td>
                      <td style={td}>{ROLE_LABEL[m.role]}</td>
                      <td style={td}><Pill tone={statusTone(m.status)}>{STATUS_LABEL[m.status]}</Pill></td>
                      <td style={{ ...td, fontSize: 12 }}>
                        {m.requested_by ?? '—'}
                        {m.requested_at ? <div style={{ fontFamily: monoFont, fontSize: 10.5, color: C.muted }}>{formatIsoCompact(m.requested_at)}</div> : null}
                      </td>
                      <td style={{ ...td, fontFamily: monoFont, fontSize: 11, whiteSpace: 'nowrap' }}>
                        {m.last_active_at ? formatIsoCompact(m.last_active_at) : '—'}
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        {isOwner && !locked ? (
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            {m.status === 'pending_approval' ? (
                              <>
                                <button type="button" disabled={busy} style={btn('lime', !busy)} onClick={() => void startAction(m, 'approve')}>Approve</button>
                                <button type="button" disabled={busy} style={btn('plain', !busy)} onClick={() => void deny(m)}>Deny</button>
                              </>
                            ) : null}
                            {m.status === 'active' ? (
                              <>
                                <select aria-label={`New role for ${m.email}`}
                                  value={roleChoice[m.staff_id] ?? (m.role === 'owner' ? 'admin' : m.role)}
                                  onChange={(e) => setRoleChoice((p) => ({ ...p, [m.staff_id]: e.target.value as Assignable }))}
                                  style={{ ...control, minHeight: 36, padding: '4px 8px', fontSize: 12 }}>
                                  {ASSIGNABLE.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                                </select>
                                <button type="button" disabled={busy || (roleChoice[m.staff_id] ?? m.role) === m.role}
                                  style={btn('plain', !busy && (roleChoice[m.staff_id] ?? m.role) !== m.role)}
                                  onClick={() => void startAction(m, 'change_role', roleChoice[m.staff_id])}>Change role</button>
                                <button type="button" disabled={busy} style={btn('coral', !busy)} onClick={() => void startAction(m, 'suspend')}>Remove</button>
                              </>
                            ) : null}
                            {m.status === 'suspended' ? (
                              <button type="button" disabled={busy} style={btn('plain', !busy)} onClick={() => void startAction(m, 'reactivate')}>Reactivate</button>
                            ) : null}
                          </div>
                        ) : (
                          <span style={{ fontSize: 11.5, color: C.dim }}>{m.is_owner ? 'Owner' : self ? 'Only the owner can change you' : isOwner ? '' : 'Owner decides'}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        </Card>
      ) : null}

      {pending !== null ? (
        <div role="dialog" aria-modal="true" aria-label="Confirm with code"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 50 }}>
          <Card style={{ padding: 20, width: '100%', maxWidth: 420 }}>
            <div style={{ fontFamily: sansFont, fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 6 }}>
              {pending.action === 'approve' ? `Approve ${pending.member.email}`
                : pending.action === 'change_role' ? `Change ${pending.member.email} to ${ROLE_LABEL[pending.role ?? 'support']}`
                : pending.action === 'suspend' ? `Remove ${pending.member.email}`
                : `Reactivate ${pending.member.email}`}
            </div>
            <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
              We emailed a 6-digit code to {pending.sent_to}. It expires in 10 minutes.
              {pending.action === 'suspend' ? ' Removing ends their sessions and support links immediately.' : ''}
            </div>
            <input aria-label="Code" inputMode="numeric" autoFocus maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              style={{ ...control, width: '100%', fontFamily: monoFont, fontSize: 20, letterSpacing: 6, textAlign: 'center' }} />
            {dialogError !== null ? <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.coralFg, marginTop: 8 }}>{dialogError}</div> : null}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
              <button type="button" style={btn('plain')} onClick={() => setPending(null)}>Cancel</button>
              <button type="button" disabled={busy || code.length !== 6}
                style={btn(pending.action === 'suspend' ? 'coral' : 'lime', !busy && code.length === 6)}
                onClick={() => void confirm()}>
                {busy ? 'Checking…' : 'Confirm'}
              </button>
            </div>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
